import PropTypes from 'prop-types';
import React, { useContext, useCallback, useState } from 'react';
import classNames from 'classnames';
import { noop } from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { IS_EMAIL_SUPPORTED_ENABLED } from '@bingads-webui-campaign-react/conversion-utils';
import { AccordionHeader } from '@bingads-webui-campaign-react/conversion-wizard-components';
import { useIsUetTag, ConversionDataContext } from '@bingads-webui-campaign-react/conversion-data';
import { ClarityGraphInstruction } from '@bingads-webui-campaign-react/clarity-graph-instruction';
import { Checkbox, Label } from '@fluentui/react';
import { getEditWebsiteViewStyle } from '../styles/edit-website-view-styles';
import { OptionsSelectableCardList } from './options-selectable-card-list';
import { OptionsChoiceGroup } from './options-choice-group';

export const EditWebsiteView = (props) => {
  const {
    onChangeOptions,
    isEmailSupported,
    isGTMEnabled,
    showClarityOption,
    onChangeClarityOption,
    permissions,
    selectedOption,
    isEventGoal,
    showTagDescriptions,
  } = props;
  const classes = useDefaultStyles(getEditWebsiteViewStyle);
  const { tagContext } = useContext(ConversionDataContext);
  const { data: tag } = tagContext || {};
  const isClarityEnabledForTag = tag && tag.tagData && tag.tagData.IsClarityEnabled;

  const i18n = useContext(I18nContext);
  const isUetTag = useIsUetTag();
  // Read the tag clarity status here
  const [clarityConsentReceived, setClarityConsentReceived] = useState(false);

  const onRenderClarityLabel = () => (
    <ClarityGraphInstruction i18n={i18n} />
  );

  const onChangeClarityStatus = useCallback((event, checked) => {
    const isEnabled = !!checked;
    setClarityConsentReceived(isEnabled);
    onChangeClarityOption(isEnabled);
  }, [onChangeClarityOption]);

  const [isBaseTagDescriptionExpanded, setIsBaseTagDescriptionExpanded] = useState(false);
  const [isEventTagDescriptionExpanded, setIsEventTagDescriptionExpanded] = useState(false);

  const handleBaseTagDescriptionExpand = useCallback((_ev, value) => {
    setIsBaseTagDescriptionExpanded(value);
  }, []);

  const handleEventTagDescriptionExpand = useCallback((_ev, value) => {
    setIsEventTagDescriptionExpanded(value);
  }, []);

  return (
    <div className={classNames(classes.container, 'edit-website-view')}>
      <div className={classNames(classes.description, 'edit-website-description')}>
        {showTagDescriptions && (
          <React.Fragment>
            <p>{i18n.getString(_TL_('To achieve your conversion goal setting, you will need to set up:'))}</p>
            <AccordionHeader
              className="base-tag-header"
              expanded={isBaseTagDescriptionExpanded}
              onChange={handleBaseTagDescriptionExpand}
              i18n={i18n}
            >
              {i18n.getString(isUetTag ? _TL_('Universal Event Tag (UET Tag)') : _TL_('Base tag'))}
            </AccordionHeader>
            {isBaseTagDescriptionExpanded && (
              <p className={classNames(classes.descriptionExpanded, 'base-tag-description')}>
                {i18n.getString(isUetTag ?
                  _TL_('Universal Event Tracking (UET) is the key that unlocks powerful Microsoft Advertising features such as conversion tracking. The key comes in the form of the UET tag, which records what customers do on your website and sends that information to Microsoft Advertising.') :
                  _TL_('Adding the base tag unlocks powerful Microsoft Advertising features such as conversion tracking. The base tag records what customers do on your website and sends that information to Microsoft Advertising.'))}
              </p>
            )}

            {isEventGoal && (
              <React.Fragment>
                <AccordionHeader
                  className="event-tag-header"
                  expanded={isEventTagDescriptionExpanded}
                  onChange={handleEventTagDescriptionExpand}
                  i18n={i18n}
                >
                  {i18n.getString(_TL_('Custom event tag'))}
                </AccordionHeader>
                {isEventTagDescriptionExpanded && (
                  <p className={classNames(classes.descriptionExpanded, 'event-tag-description')}>
                    {i18n.getString(isUetTag ?
                      _TL_('Custom event tag is an additional custom event JavaScript code you need to add to your UET tag, enabling the UET tag to track specific actions such as clicking on the "Download now" button.') :
                      _TL_('Custom event tag is an additional custom event JavaScript code you need to set up in addition to the base tag. This will help you track specific actions such as clicking on the "Download now" button.'))}
                  </p>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
        {!showTagDescriptions && i18n.getString(_TL_('To track customer actions, you\'ll need to add a tracking tag to your website\'s code. You can edit the code yourself or send the tag to someone else.'))}
      </div>

      {showTagDescriptions ?
        <OptionsSelectableCardList
          isGTMEnabled={isGTMEnabled}
          onChangeOptions={onChangeOptions}
          isEmailSupported={isEmailSupported}
          permissions={permissions}
          selectedOption={selectedOption}
        /> :
        <OptionsChoiceGroup
          isGTMEnabled={isGTMEnabled}
          onChangeOptions={onChangeOptions}
          isEmailSupported={isEmailSupported}
          permissions={permissions}
          selectedOption={selectedOption}
        />}

      {showClarityOption && !isClarityEnabledForTag &&
      <React.Fragment>
        <Label>{i18n.getString(_TL_('Enable Microsoft Clarity'))}</Label>
        <Checkbox
          className={classNames(classes.section, 'clarity-checkbox')}
          label={i18n.getString(_TL_('Microsoft Clarity brings advertisers actionable user behavioral insights to optimize their ad campaigns. By enabling Clarity tracking, you agree to the Clarity terms of use and privacy policy. Learn more'))}
          onRenderLabel={onRenderClarityLabel}
          checked={clarityConsentReceived}
          onChange={onChangeClarityStatus}
        />
      </React.Fragment>}
    </div>
  );
};

EditWebsiteView.propTypes = {
  onChangeOptions: PropTypes.func.isRequired,
  isEmailSupported: PropTypes.bool,
  permissions: PropTypes.shape({ }).isRequired,
  showClarityOption: PropTypes.bool.isRequired,
  isGTMEnabled: PropTypes.bool.isRequired,
  selectedOption: PropTypes.string,
  isEventGoal: PropTypes.bool,
  showTagDescriptions: PropTypes.bool,
  onChangeClarityOption: PropTypes.func,
};

EditWebsiteView.defaultProps = {
  isEmailSupported: IS_EMAIL_SUPPORTED_ENABLED,
  selectedOption: undefined,
  isEventGoal: false,
  showTagDescriptions: false,
  onChangeClarityOption: noop,
};

